/**
 * @fileoverview Conteudo estatico do "Guia das automacoes da IA".
 *
 * Cada entrada representa uma tool ativavel por toggle no node Agente IA
 * e contem: o que faz, quando usar, exemplo em bolhas de chat e (quando
 * aplicavel) quando NAO usar. Conteudo escrito em portugues claro para
 * operadores leigos — sem jargao tecnico.
 *
 * O `id` casa com o nome do toggle equivalente no ConfigPanel para
 * facilitar futura navegacao direta a uma tool especifica.
 */
import {
  UserCheck,
  CheckCircle2,
  ArrowRightCircle,
  Tag,
  StickyNote
} from "lucide-react";

const AI_TOOLS_GUIDE = [
  {
    id: "transfer_agent",
    title: "Transferir para atendente",
    icon: UserCheck,
    whatItDoes:
      "Transfere o atendimento para um atendente humano. Você pode selecionar UM ou MAIS atendentes destino. Quando há vários, escolha o modo de distribuição (Aleatório, Balanceado pela carga atual de tickets, ou Percentual com pesos). A IA para de responder e o atendente assume.",
    whenToUse: [
      "Cliente pede explicitamente para falar com gerente, atendente, humano ou supervisor",
      "Cliente está irritado, agressivo ou ameaça Procon/redes sociais",
      "Cliente menciona Ordem de Serviço (OS), CPF para consulta, ou andamento de aparelho",
      "Negociação de preço, desconto ou condição especial fora do catálogo",
      "Cliente esqueceu objeto pessoal na loja"
    ],
    example: [
      { side: "client", text: "preciso falar com um humano, isso aqui não tá funcionando" },
      {
        side: "ai",
        text: "Vou te transferir para um atendente que vai te ajudar, um momento.",
        effect: "🙋 Transferido para a Ana (sorteado entre Ana, Gustavo, João — modo Aleatório)"
      },
      { side: "client", text: "[outro cliente, 5 minutos depois] preciso falar com gerente" },
      {
        side: "ai",
        text: "Vou te transferir para um atendente, um momento.",
        effect: "🙋 Transferido para o Gustavo (Ana já está com 3 tickets — modo Balanceado escolheu o menos sobrecarregado)"
      }
    ],
    whenNotToUse: [
      "Para perguntas simples que a IA consegue responder sozinha",
      "Logo no início da conversa, antes de tentar entender o que o cliente quer"
    ]
  },
  {
    id: "auto_resolve",
    title: "Encerrar atendimento",
    icon: CheckCircle2,
    whatItDoes:
      "Fecha automaticamente o ticket quando o cliente confirmar que não precisa de mais nada. Sem essa automação, o ticket fica aberto eternamente e ocupa lugar na fila.",
    whenToUse: [
      "Cliente disse 'é só isso', 'obrigado', 'pode encerrar', 'tchau' ou similar",
      "Após a IA tirar a dúvida e oferecer 'precisa de mais alguma coisa?', e o cliente confirmar que não",
      "Conversa naturalmente chegou ao fim"
    ],
    example: [
      { side: "client", text: "vocês abrem no sábado?" },
      { side: "ai", text: "Atendemos de segunda a sexta, das 8:30h às 18h." },
      { side: "ai", text: "Precisa de mais alguma coisa?" },
      { side: "client", text: "não, era só isso. obrigado!" },
      {
        side: "ai",
        text: "Por nada, tenha um ótimo dia!",
        effect: "🔒 Ticket fechado"
      }
    ],
    whenNotToUse: [
      "Se o cliente fez uma nova pergunta após 'obrigado' — continue atendendo",
      "Se a conversa ainda está em andamento e o cliente não confirmou o encerramento"
    ]
  },
  {
    id: "transfer_queue",
    title: "Transferir para fila",
    icon: ArrowRightCircle,
    whatItDoes:
      "Envia o ticket para uma FILA inteira (Comercial, Garantia, Suporte etc.) — qualquer atendente daquela fila pode pegar. Diferente de 'Transferir para atendente', que aponta para uma pessoa específica.",
    whenToUse: [
      "Cliente quer orçamento → fila Comercial",
      "Cliente fala de problema de garantia → fila Garantia",
      "Assunto é de uma área inteira, não de pessoa específica",
      "Quando você quer que qualquer atendente disponível daquela fila atenda"
    ],
    example: [
      { side: "client", text: "queria saber o valor da troca de tela do meu S24" },
      {
        side: "ai",
        text: "Vou te direcionar para nossa equipe Comercial, um momento.",
        effect: "🔁 Transferido para a fila Comercial"
      }
    ],
    whenNotToUse: [
      "Quando o caso já tem um atendente responsável conhecido — use 'Transferir para atendente'",
      "Quando a IA consegue responder sem precisar de humano"
    ]
  },
  {
    id: "tag_ticket",
    title: "Etiquetar atendimento",
    icon: Tag,
    whatItDoes:
      "Aplica uma ou mais etiquetas (tags) ao ticket para categorização e analytics. O cliente NÃO vê nada — é metadado interno. Apenas etiquetas cadastradas em /tags são aceitas; nomes inexistentes são ignorados.",
    whenToUse: [
      "A qualquer momento, ao identificar uma categoria clara para o atendimento",
      "Após o cliente confirmar intenção de compra → etiqueta 'lead-quente'",
      "Quando o atendimento é sobre garantia → etiqueta 'garantia'",
      "Pode aplicar várias etiquetas de uma vez (ex: lead-quente + troca-tela + samsung)"
    ],
    example: [
      { side: "client", text: "tô interessado em trocar a tela do meu Galaxy S24 Ultra" },
      {
        side: "ai",
        text: "Perfeito! Vou anotar aqui pra equipe comercial te atender.",
        effect: "🏷️ Etiquetas aplicadas: lead-quente, troca-tela, samsung-s24"
      }
    ]
  },
  {
    id: "add_note",
    title: "Adicionar nota interna",
    icon: StickyNote,
    whatItDoes:
      "Deixa uma nota privada no ticket, visível APENAS para atendentes humanos — o cliente não recebe nada. Útil para o atendente abrir o ticket já sabendo o contexto da conversa.",
    whenToUse: [
      "ANTES de transferir o ticket — deixe um resumo do problema para o atendente",
      "Quando dados sensíveis foram coletados (CPF, número da OS, preferência de pagamento)",
      "Para registrar observações úteis (ex: 'cliente parece desconfiado sobre garantia')"
    ],
    example: [
      { side: "client", text: "esqueci meu carregador na loja ontem à tarde" },
      {
        side: "ai",
        text: "Vou te transferir para um atendente verificar isso, um momento.",
        effect: "📝 Nota interna: \"Cliente esqueceu carregador na loja em 12/05 à tarde. Verificar achados e perdidos.\""
      },
      {
        side: "ai",
        text: "",
        effect: "🙋 Transferido para o atendente Maria"
      }
    ],
    whenNotToUse: [
      "NÃO use para responder ao cliente — para isso, envie uma mensagem normal",
      "Limite de 255 caracteres por nota — seja conciso e direto"
    ]
  }
];

export default AI_TOOLS_GUIDE;
